/**
 * Site Context
 * Provides site settings, theme, branding, and navigation across all components
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import siteContentApi from '../services/siteContent';
import { resolveTenantAssetUrl } from '../utils/tenantAssetUrls';
import {
  isCssColorValue,
  resolveHeaderChrome,
  tenantHasEcommerce,
} from '../utils/storefrontChrome';

// Default values
const defaultSiteContext = {
  // Loading state
  loading: true,
  error: null,
  
  // Tenant info
  tenantId: null,
  // subscription_plans.tier_level bitmask (m084); ecommerce chrome when bit 4 set
  tierLevel: 0,
  hasEcommerce: false,
  // Site Builder fidelity P2 header chrome (defaults = Header.css / half-split)
  logoPlacement: 'left',
  navPrimaryCount: null,
  // Site Builder fidelity P3 — blank = structured footer only (HFF-safe)
  footerHtml: '',
  
  // Site settings
  siteName: '',
  tagline: '',
  logoUrl: null,
  faviconUrl: null,
  
  // Theme & colors
  theme: null,
  colors: {
    primary: '#4a6741',
    secondary: '#8b7355',
    accent: '#d4a574',
    background: '#fdfbf7',
    backgroundAlt: '#f5f1eb',
    text: '#333333',
    textLight: '#666666',
    border: '#e0d5c7'
  },
  fonts: {
    heading: 'Playfair Display',
    body: 'Open Sans'
  },
  
  // Contact & social
  contactInfo: { phone: '', email: '', address: '' },
  socialLinks: { facebook: '', instagram: '', twitter: '', linkedin: '' },
  businessHours: [],

  // Commerce
  taxRate: null,
  // Feature C C-4.3: shipping_rates (m145 JSONB), keyed by shipping_zone
  // (not-shippable | in-state | in-country | no-restrictions) -> dollar rate.
  // Empty object => every zone falls back to the legacy flat rate in Checkout.
  shippingRates: {},
  // Feature C C-4 fix: tenant Stripe connected-account id. Checkout initializes
  // Stripe.js with { stripeAccount } so the inline PI confirm targets the same
  // connected account the server created the PI on.
  stripeAccountId: null,
  
  // SEO defaults
  defaultSeoTitle: '',
  defaultSeoDescription: '',
  
  // Navigation
  navigation: [],
  pages: [],
  
  // Methods
  refreshSettings: () => {},
  getPageBySlug: () => null
};

const SiteContext = createContext(defaultSiteContext);

/**
 * Hook to access site context
 */
export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

/**
 * Site Provider Component
 * Wraps the app and provides site data to all children
 */
export const SiteProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [theme, setTheme] = useState(null);
  const [pages, setPages] = useState([]);
  const [navigation, setNavigation] = useState([]);
  const [replacements, setReplacements] = useState({});

  // Load site settings and theme on mount
  const loadSiteData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch site settings
      const siteSettings = await siteContentApi.getSiteSettings();
      
      if (!siteSettings) {
        setError('Failed to load site settings');
        setLoading(false);
        return;
      }
      
      setSettings(siteSettings);
      
      // Fetch theme if we have a theme_id
      if (siteSettings.theme_id) {
        const themeData = await siteContentApi.getTheme(siteSettings.theme_id);
        setTheme(themeData);
      }
      
      // Fetch published pages
      const pagesData = await siteContentApi.getPages();
      setPages(pagesData);
      
      // Fetch navigation. A published substitute for a built-in page arrives
      // on replacements even when that page is not in the menu.
      const navData = await siteContentApi.getNavigation();
      const navItems = Array.isArray(navData) ? navData : (navData.items || []);
      const navReplacements = Array.isArray(navData) ? {} : (navData.replacements || {});
      setNavigation(navItems);
      setReplacements(navReplacements);
      
    } catch (err) {
      console.error('Error loading site data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSiteData();
  }, [loadSiteData]);

  // Apply CSS custom properties for colors
  useEffect(() => {
    if (!settings) return;
    
    const colors = siteContentApi.getComputedColors(theme, settings.color_overrides);
    const fonts = siteContentApi.getComputedFonts(theme, settings.font_overrides);
    
    const root = document.documentElement;
    const overrides = settings.color_overrides || {};

    Object.entries(colors).forEach(([key, value]) => {
      if (!isCssColorValue(value)) return;
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      root.style.setProperty(`--color-${cssKey}`, value);
    });

    if (colors.primary) {
      root.style.setProperty('--brand-color', colors.primary);
    }

    // P2: discrete logo_* columns win; color_overrides chrome keys remain a fallback.
    const chrome = resolveHeaderChrome(settings, overrides);
    if (chrome.headerBackground) {
      root.style.setProperty('--header-bg', chrome.headerBackground);
      root.style.setProperty('--header-shadow', 'none');
    } else {
      root.style.removeProperty('--header-bg');
      root.style.removeProperty('--header-shadow');
    }
    if (chrome.logoHeight) {
      const h = chrome.logoHeight.includes('px') ? chrome.logoHeight : `${chrome.logoHeight}px`;
      root.style.setProperty('--header-logo-height', h);
    } else {
      root.style.removeProperty('--header-logo-height');
    }
    if (chrome.logoMaxWidth) {
      const w = chrome.logoMaxWidth.includes('px') ? chrome.logoMaxWidth : `${chrome.logoMaxWidth}px`;
      root.style.setProperty('--header-logo-max-width', w);
    } else {
      root.style.removeProperty('--header-logo-max-width');
    }

    root.style.setProperty('--font-heading', fonts.heading);
    root.style.setProperty('--font-body', fonts.body);
    
    // Update document title
    if (settings.site_name) {
      document.title = settings.default_seo_title || settings.site_name;
    }
    
    // Update meta description
    if (settings.default_seo_description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = settings.default_seo_description;
    }
    
    // Update favicon if provided
    if (settings.favicon_url) {
      const faviconHref = resolveTenantAssetUrl(settings.favicon_url);
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = faviconHref;
    }
    
  }, [settings, theme]);

  // Find a page by its slug
  const getPageBySlug = useCallback((slug) => {
    return pages.find(p => p.slug === slug) || null;
  }, [pages]);

  // Normalize the tenant tax rate to a Number (or null). The backend returns
  // NUMERIC(5,4) which serializes as a string like "0.0825".
  const rawTaxRate = settings?.tax_rate;
  const taxRate =
    rawTaxRate === null || rawTaxRate === undefined || rawTaxRate === ''
      ? null
      : Number(rawTaxRate);

  const tierLevel = Number(settings?.tier_level) || 0;
  const hasEcommerce = tenantHasEcommerce(tierLevel);
  const headerChrome = resolveHeaderChrome(
    settings || {},
    settings?.color_overrides || {}
  );

  // Compute the context value
  const contextValue = {
    loading,
    error,
    
    tenantId: settings?.tenant_id || null,
    tierLevel,
    hasEcommerce,
    logoPlacement: headerChrome.logoPlacement === 'center' ? 'center' : 'left',
    navPrimaryCount: headerChrome.navPrimaryCount,
    footerHtml: settings?.footer_html || '',
    
    siteName: settings?.site_name || '',
    tagline: settings?.tagline || '',
    logoUrl: resolveTenantAssetUrl(settings?.logo_url) || null,
    faviconUrl: resolveTenantAssetUrl(settings?.favicon_url) || null,
    
    theme,
    colors: siteContentApi.getComputedColors(theme, settings?.color_overrides),
    fonts: siteContentApi.getComputedFonts(theme, settings?.font_overrides),
    
    contactInfo: settings?.contact_info || { phone: '', email: '', address: '' },
    socialLinks: settings?.social_links || { facebook: '', instagram: '', twitter: '', linkedin: '' },
    businessHours: settings?.business_hours || [],

    taxRate: Number.isFinite(taxRate) ? taxRate : null,
    // Feature C C-4.3: tenant shipping rates by zone (m145). Defaults to {} so
    // Checkout's resolver falls back to the legacy flat rate per unconfigured zone.
    shippingRates: settings?.shipping_rates || {},
    // Feature C C-4 fix: connected-account id for inline PI confirmation.
    stripeAccountId: settings?.stripe_account_id || null,
    
    defaultSeoTitle: settings?.default_seo_title || '',
    defaultSeoDescription: settings?.default_seo_description || '',
    
    navigation,
    replacements,
    pages,
    
    refreshSettings: loadSiteData,
    getPageBySlug
  };

  // Show loading screen until settings are loaded to prevent color flash
  if (loading) {
    return (
      <SiteContext.Provider value={contextValue}>
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fdfbf7', // Neutral background
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e0e0e0',
            borderTopColor: '#4a6741',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </SiteContext.Provider>
    );
  }

  return (
    <SiteContext.Provider value={contextValue}>
      {children}
    </SiteContext.Provider>
  );
};

export default SiteContext;
